import { RegexConst } from '@nocowanie/core/consts/regex';
import { CancellationPolicyType, ImageRatio, ImageSize } from '@nocowanie/core/enums';
import { imageHelpers } from '@nocowanie/core/helpers';

import { META_IMAGE_DIMENSIONS } from '@app/consts/image.consts';
import { calculatePolicyInfo } from '@app/hooks/usePolicyInfo';
import { apiTypes } from '@app/models';

export const SCHEMA_URL = 'https://schema.org';

export const trimDescription = (description: string) => {
    // Strip HTML tags and markdown to plain text for JSON-LD.
    // CMS returns markdown (AI descriptions) or HTML (regular descriptions).
    const desc = description
        .replace(RegexConst.HTML_TAGS, ' ')
        .replace(RegexConst.MARKDOWN_BOLD_ITALIC, '$1')
        .replace(/\s+/g, ' ')
        .trim();

    return desc ? (desc.length > 300 ? `${desc.substring(0, 300)}...` : desc) : undefined;
};

export const checkIfPetsAllowed = ({
    labels,
    internalAmenityGroups,
    services,
}: {
    labels: apiTypes.Label[];
    internalAmenityGroups: apiTypes.InternalAmenityGroup[];
    services: apiTypes.Service[];
}) => {
    const petsInLabels = labels.filter(label => label == apiTypes.Label['PetFriendly']);
    const petsInAmenities = internalAmenityGroups.filter(
        group => group.type == apiTypes.InternalAmenityGroupEnum['PetFriendly'],
    );
    const petsInServices = services.filter(
        service =>
            service.type == apiTypes.ServiceType['PetFriendly'] ||
            service.type == apiTypes.ServiceType['PetsAreAllowed'],
    );

    return !!petsInLabels.length || !!petsInAmenities.length || !!petsInServices.length;
};

export const createImageVariants = (image: string) => [
    imageHelpers.wpresizer(META_IMAGE_DIMENSIONS, image),
    imageHelpers.wpresizer({ ratio: ImageRatio['4/3'], size: ImageSize.Xl }, image),
    imageHelpers.wpresizer({ ratio: ImageRatio['16/9'], size: ImageSize.Xl }, image),
];

export const createAddress = ({
    addressCountry,
    addressLocality,
    addressRegion,
    postalCode,
    streetAddress,
}: Partial<{
    addressCountry: string;
    addressLocality: string;
    addressRegion: string;
    postalCode: string;
    streetAddress: string;
}>) => {
    if (!addressCountry && !addressLocality && !addressRegion && !postalCode && !streetAddress) {
        return null;
    }

    const transformAddressRegion = () => {
        if (addressRegion?.toLowerCase() === 'mazowsze') {
            return 'Mazowieckie';
        }

        return addressRegion;
    };

    return {
        '@type': 'PostalAddress',
        ...(addressCountry && { addressCountry }),
        ...(addressLocality && { addressLocality }),
        ...(addressRegion && { addressRegion: transformAddressRegion() }),
        ...(postalCode && { postalCode }),
        ...(streetAddress && { streetAddress }),
    };
};

export const createGeo = ({ latitude, longitude }: { latitude: number; longitude: number }) => ({
    '@type': 'GeoCoordinates',
    latitude,
    longitude,
});

export const createAggregateRating = ({
    ratingAverage,
    ratingCount,
}: {
    ratingAverage: number;
    ratingCount: number;
}) => {
    const BEST_RATING = 10;
    const WORST_RATING = 1;

    return {
        '@type': 'AggregateRating',
        ratingValue: ratingAverage,
        ratingCount,
        bestRating: BEST_RATING,
        worstRating: WORST_RATING,
    };
};

export const createAmenityFeatures = ({ services }: { services: apiTypes.Service[] }) =>
    services.map(({ name }) => ({
        '@type': 'LocationFeatureSpecification',
        name: name,
        value: true,
    }));

const createFoodService = ({ type }: { type: 'breakfast' | 'dinner' }) => ({
    '@type': 'TypeAndQuantityNode',
    typeOfGood: {
        '@type': 'FoodService',
        name: type === 'breakfast' ? 'BreakfastIncluded' : 'DinnerIncluded',
    },
});

export const createLodgingReservationOffer = ({
    withTopLevelPrice,
    identifierIndex,
    checkInStartDate,
    checkOutStartDate,
    checkInStartTime,
    checkOutStartTime,
    priceCurrency,
    apartment,
    variant,
}: {
    withTopLevelPrice?: boolean;
    identifierIndex: number;
    checkInStartDate: string;
    checkOutStartDate: string;
    checkInStartTime: string;
    checkOutStartTime: string;
    priceCurrency: apiTypes.Currency;
    apartment: apiTypes.Apartment;
    variant: apiTypes.Variant;
}) => {
    const VAT_RATE = 0.23;

    const checkinTime = `${checkInStartDate}T${checkInStartTime}:00`;
    const checkoutTime = `${checkOutStartDate}T${checkOutStartTime}:00`;

    const grossPrice = variant.convertedTotalPrice ?? variant.totalPrice ?? variant.rate;
    const netPrice = Math.round(grossPrice / (1 + VAT_RATE));
    const vatPrice = grossPrice - netPrice;

    const provisionType = variant.provision.type;

    return {
        '@type': ['Offer', 'LodgingReservation'],
        identifier: `${apartment.id}-${identifierIndex}`,
        checkinTime,
        checkoutTime,
        availability: `${SCHEMA_URL}/InStock`,
        ...(withTopLevelPrice && { price: grossPrice, priceCurrency }),
        priceSpecification: {
            '@type': 'CompoundPriceSpecification',
            price: grossPrice,
            priceCurrency,
            valueAddedTaxIncluded: true,
            priceComponent: [
                {
                    '@type': 'UnitPriceSpecification',
                    name: '',
                    price: netPrice,
                    priceCurrency,
                },
                {
                    '@type': 'UnitPriceSpecification',
                    name: 'GenericTax',
                    price: vatPrice,
                    priceCurrency,
                    priceComponentType: 'GenericTax',
                },
            ],
        },
        ...(provisionType !== apiTypes.ProvisionType.NoMeal && {
            includesObject:
                provisionType === apiTypes.ProvisionType.Breakfast
                    ? [createFoodService({ type: 'breakfast' })]
                    : [
                          createFoodService({ type: 'breakfast' }),
                          createFoodService({ type: 'dinner' }),
                      ],
        }),
        hasMerchantReturnPolicy: (() => {
            const policyInfo = calculatePolicyInfo(variant.cancellationRules);

            if (policyInfo.policyType === CancellationPolicyType.NoRefund) {
                return {
                    '@type': 'MerchantReturnPolicy',
                    returnPolicyCategory: `${SCHEMA_URL}/MerchantReturnNotPermitted`,
                };
            }

            return {
                '@type': 'MerchantReturnPolicy',
                returnPolicyCategory: `${SCHEMA_URL}/MerchantReturnFiniteReturnWindow`,
                refundType: `${SCHEMA_URL}/${policyInfo.policyPenalty ? 'PartialRefund' : 'FullRefund'}`,
                merchantReturnDays: policyInfo.policyDate,
                restockingFee: policyInfo.policyPenalty ?? 0,
            };
        })(),
    };
};

export const createHotelRoomProduct = ({
    apartment,
    ...rest
}: Omit<
    Parameters<typeof createLodgingReservationOffer>[number],
    'withTopLevelPrice' | 'identifierIndex' | 'variant'
>) => {
    const identifier = apartment.id;
    const title = apartment.name;
    const image = apartment.images?.find(image => image?.default)?.url;

    const variants = apartment.variants?.filter(variant => !!variant) ?? [];

    return {
        '@type': ['HotelRoom', 'Product'],
        name: title,
        identifier,
        image,
        description: title,
        occupancy: { '@type': 'QuantitativeValue', value: apartment.maxAdults },
        offers:
            variants.length === 1
                ? createLodgingReservationOffer({
                      withTopLevelPrice: true,
                      identifierIndex: 0,
                      apartment,
                      variant: variants[0],
                      ...rest,
                  })
                : variants.map((variant, identifierIndex) =>
                      createLodgingReservationOffer({
                          withTopLevelPrice: true,
                          identifierIndex,
                          apartment,
                          variant,
                          ...rest,
                      }),
                  ),
    };
};
